import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
    TextField,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    AppBar,
    Checkbox,
    LinearProgress,
    CircularProgress,
    Select,
    FormControl,
    InputLabel,
    MenuItem,
    Tooltip,
    Typography,
} from '@material-ui/core';
import {
    Add,
    Search,
    CloudUpload,
    CloudDownload,
    Edit,
    Check as CheckIcon,
} from '@material-ui/icons';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
    bulkDisableProducts,
    bulkEnableProducts,
    deleteBulkProduct,
    getDownloadProducts,
    getProductsService,
    updateProductService,
    uploadBatchProductImages,
} from '../../services/products';
import { useStyles } from './ProductsTablePage.styles';
import ButtonUpgradePlan from '../../components/ButtonUpgradePlan';
import { handleImageBase64 } from '../../utils/handle-base64';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { routes, formatNumber } from '../../constants.js/misc';
import ModalUpgradePlan from '../../components/ModalUpgradePlan';
import ConfirmableButton from '../../components/ConfirmableButton';
import { colors } from '../../constants.js/colors';
import { useDebounce } from '../../utils/app';
import Filters from './components/Filters';
import { getStoreService } from '../../services/store';

function ProductsTablePageMobile({ storeData, storeId }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();

    const fileInputRef = useRef(null);
    const [bulkMode, setBulkMode] = useState(false);
    const [selected, setSelected] = useState([]);
    const [products, setProducts] = useState([]);
    const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [gettingData, setGettingData] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [totalProducts, setTotalProducts] = useState(0);
    const [activeProducts, setActiveProducts] = useState(0);
    const [hiddenProducts, setHiddenProducts] = useState(0);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [isFreemium, setIsFreemium] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [maxProductLimit, setMaxProductLimit] = useState(20);
    const [selectingAll, setSelectingAll] = useState(false);

    const debouncedSearch = useDebounce(searchQuery, 500);

    const handleSelectProduct = (id) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const handleToggleBulk = () => {
        setBulkMode(!bulkMode);
        if (!bulkMode) setSelected([]);
    };

    const toggleProduct = (selectedProduct) => {
        setGettingData(true);
        const newActiveStatus = selectedProduct.active ? 0 : 1;
        updateProductService(
            storeId,
            {
                active: newActiveStatus,
            },
            selectedProduct.id,
            true
        )
            .then(() => {
                setGettingData(false);
                setProducts(
                    products.map((product) => ({
                        ...product,
                        active:
                            selectedProduct.id === product.id ? newActiveStatus : product.active,
                    }))
                );
                enqueueSnackbar(
                    newActiveStatus
                        ? t('ProductsTable.toggleSuccesActive')
                        : t('ProductsTable.toggleSuccesInactive'),
                    {
                        variant: 'success',
                        autoHideDuration: 2500,
                    }
                );
            })
            .catch((err) => {
                setGettingData(false);
                enqueueSnackbar(
                    newActiveStatus
                        ? t('ProductsTable.toggleErrorActive')
                        : t('ProductsTable.toggleErrorInactive'),
                    {
                        variant: 'error',
                        autoHideDuration: 2500,
                    }
                );
                console.log(err);
            });
    };

    const handleBulkDelete = async () => {
        await deleteBulkProduct(storeId, selected);
        setSelected([]);
        localStorage.removeItem('selectedProducts');
        fetchProducts(0, itemsPerPage);
    };

    const handleBulkAction = async (action) => {
        if (!selected.length) return;

        const activeValue = action === 'activate' ? 1 : 0;
        const idsToUpdate = selected.filter((id) => {
            const product = products.find((item) => item.id === id);
            if (!product) return true;
            return !(
                product.is_hidden_by_limit &&
                storeData?.plan_features?.hide_excess_products
            );
        });

        if (!idsToUpdate.length) return;

        setGettingData(true);
        try {
            if (activeValue === 1) {
                await bulkEnableProducts(storeId, idsToUpdate);
            } else {
                await bulkDisableProducts(storeId, idsToUpdate);
            }

            setProducts((currentProducts) =>
                currentProducts.map((product) =>
                    idsToUpdate.includes(product.id)
                        ? { ...product, active: activeValue }
                        : product
                )
            );

            enqueueSnackbar(
                activeValue
                    ? t('ProductsTable.bulkActivateSuccess')
                    : t('ProductsTable.bulkDeactivateSuccess'),
                { variant: 'success', autoHideDuration: 2500 }
            );
        } catch (err) {
            enqueueSnackbar(
                activeValue
                    ? t('ProductsTable.bulkActivateError')
                    : t('ProductsTable.bulkDeactivateError'),
                { variant: 'error', autoHideDuration: 2500 }
            );
            console.error(err);
        } finally {
            setGettingData(false);
        }
    };

    const fetchProducts = async (pageNumber = 0, limit = 10, append = false) => {
        setGettingData(true);

        try {
            const filters = {};

            // Add filters only if they exist
            if (selectedCategories && selectedCategories.id) {
                filters.categoryId = selectedCategories.id;
            }
            if (selectedSubcategory && selectedSubcategory.id) {
                filters.categoryId = selectedSubcategory.id;
            }

            if (selectedStatus !== null) {
                // assuming active = 1, inactive = 0
                filters.active = selectedStatus === 1 ? 1 : 0;
            }

            if (selectedOrder === 'asc' || selectedOrder === 'desc') {
                filters.order = selectedOrder;
            }

            const res = await getProductsService(
                storeId,
                limit,
                pageNumber,
                debouncedSearch,
                filters.active,
                filters.order ? 'name' : null,
                filters.order || null,
                filters.categoryId
            );
            const newProducts = res.data;

            if (append) {
                setProducts((prev) => [...prev, ...newProducts]);
            } else {
                setProducts(newProducts);
            }

            const totalCount = +res.headers['x-total-count'] || 0;
            setTotalRows(totalCount);
            setActiveProducts(newProducts.filter((p) => p.active === 1).length);
            setHiddenProducts(newProducts.filter((p) => p.active === 0).length);

            // Detect if more data exists
            const fetchedCount = (pageNumber - 1) * limit + newProducts.length;
            setHasMore(fetchedCount < totalCount);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setGettingData(false);
            setLoadingMore(false);
        }
    };

    const handleLoadMore = () => {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
        const nextPage = page + 1;
        setPage(nextPage);
        fetchProducts(nextPage, itemsPerPage, true);
    };

    const getOrderedImages = (images) => {
        return (
            images.sort((a, b) => {
                const aOrder = a.order || 0;
                const bOrder = b.order || 0;
                return aOrder - bOrder;
            })[0]?.picture_url || ''
        );
    };

    const handleExportProducts = async () => {
        if (isFreemium) {
            setOpenUpgradeModal(true);
            return;
        }
        await getDownloadProducts(storeId);
    };

    const handleBulkImportProducts = () => {
        if (isFreemium) {
            setOpenUpgradeModal(true);
            return;
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target?.files?.[0];
        if (!file) return;

        if (!file.type.includes('zip') && !file.name.endsWith('.zip')) {
            enqueueSnackbar(t('ExportImageBatch.ZipFileRequired'), {
                variant: 'error',
                autoHideDuration: 2500,
            });
            return;
        }

        setGettingData(true);

        try {
            await uploadBatchProductImages(storeId, file);
            setGettingData(false);
            enqueueSnackbar(t('ExportImageBatch.UploadSuccess'), {
                variant: 'success',
                autoHideDuration: 4000,
            });
            await fetchProducts(0);
        } catch (error) {
            setGettingData(false);
            enqueueSnackbar(t('ExportImageBatch.UploadError'), {
                variant: 'error',
                autoHideDuration: 2500,
            });
            console.error(error);
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const fetchAllFilteredProductIds = useCallback(async () => {
        const filters = {};

        if (selectedCategories && selectedCategories.id) {
            filters.categoryId = selectedCategories.id;
        }
        if (selectedSubcategory && selectedSubcategory.id) {
            filters.categoryId = selectedSubcategory.id;
        }
        if (selectedStatus !== null) {
            filters.active = selectedStatus === 1 ? 1 : 0;
        }

        const sortField = selectedOrder === 'asc' || selectedOrder === 'desc' ? 'name' : null;
        const sortOrder = selectedOrder === 'asc' || selectedOrder === 'desc' ? selectedOrder : null;
        const pageSize = 100;
        const allIds = [];
        let currentPage = 0;
        let total = Infinity;

        while (allIds.length < total) {
            const res = await getProductsService(
                storeId,
                pageSize,
                currentPage,
                debouncedSearch,
                filters.active,
                sortField,
                sortOrder,
                filters.categoryId
            );

            total = +res.headers['x-total-count'] || 0;
            const batch = (res.data || []).map((product) => product.id);

            if (!batch.length) {
                break;
            }

            allIds.push(...batch);
            currentPage += 1;

            if (allIds.length >= total) {
                break;
            }
        }

        return [...new Set(allIds)];
    }, [
        storeId,
        debouncedSearch,
        selectedCategories,
        selectedSubcategory,
        selectedStatus,
        selectedOrder,
    ]);

    const handleSelectAllClick = async () => {
        if (selectingAll || gettingData || totalRows === 0) {
            return;
        }

        setSelectingAll(true);

        try {
            const filteredIds = await fetchAllFilteredProductIds();
            const allFilteredSelected =
                filteredIds.length > 0 &&
                filteredIds.every((id) => selected.includes(id));

            const updated = allFilteredSelected
                ? selected.filter((id) => !filteredIds.includes(id))
                : [...new Set([...selected, ...filteredIds])];

            setSelected(updated);
            localStorage.setItem('selectedProducts', JSON.stringify(updated));
        } catch (err) {
            enqueueSnackbar(t('ProductsTable.error'), {
                variant: 'error',
                autoHideDuration: 2500,
            });
            console.error(err);
        } finally {
            setSelectingAll(false);
        }
    };

    const handleSelectAllToggle = () => {
        handleSelectAllClick();
    };

    const selectedCount = selected.length;
    const isAllSelected = totalRows > 0 && selectedCount === totalRows;
    const isSomeSelected = selectedCount > 0 && selectedCount < totalRows;

    useEffect(() => {
        fetchProducts(0, itemsPerPage);
    }, [debouncedSearch, selectedStatus, selectedOrder, selectedCategories, selectedSubcategory, itemsPerPage]);

    useEffect(() => {
        const fetchStore = async () => {
            try {
                const res = await getStoreService(storeId);
                setIsFreemium(
                    res.data?.store_plan?.plan?.name?.toLowerCase() === 'freemium'
                );
                setMaxProductLimit(
                    res.data?.store_plan?.plan?.limits?.max_products ?? 20
                );
            } catch (err) {
                console.error('Failed to fetch store data', err);
            }
        };
        fetchStore();
    }, [storeId, isFreemium, maxProductLimit, totalRows]);

    // Load selected IDs from localStorage on mount
    useEffect(() => {
        const savedSelected = JSON.parse(localStorage.getItem('selectedProducts') || '[]');
        setSelected(savedSelected);
    }, []);

    // Save selected IDs to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('selectedProducts', JSON.stringify(selected));
    }, [selected]);

    return (
        <Box className={classes.mobileRoot}>
            {/* Header */}
            <AppBar position="sticky" className={classes.header} elevation={0}>
                <Box className={classes.headerContent}>
                    <TextField
                        fullWidth
                        placeholder="Buscar productos"
                        variant="outlined"
                        size="small"
                        className={classes.searchBox}
                        InputProps={{
                            startAdornment: <Search style={{ marginRight: 8 }} />,
                        }}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Box className={classes.buttonGroup}>
                        {searchQuery === '' && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Add />}
                                fullWidth
                                onClick={() => {
                                    if (
                                        typeof window !== 'undefined' &&
                                        window.sessionStorage.getItem('freemium-block-add-products') === '1'
                                    ) {
                                        enqueueSnackbar(
                                            'Tu plan Freemium permite hasta 20 productos. Mejora tu plan para agregar más.',
                                            {
                                                variant: 'info',
                                                autoHideDuration: 4000,
                                            }
                                        );
                                        return;
                                    }
                                    history.push(routes.createProduct);
                                }}
                            >
                                Agregar producto
                            </Button>
                        )}
                        <Button variant="outlined" onClick={handleToggleBulk}>
                            {bulkMode ? 'Cancelar' : 'Seleccionar'}
                        </Button>
                    </Box>
                </Box>
            </AppBar>
            {/* Bulk Top Bar */}
            {bulkMode && (
                <Box className={classes.bulkBar}>
                    <Button color="inherit" size="small" onClick={handleToggleBulk}>
                        Cancelar
                    </Button>
                    <Button
                        color="inherit"
                        size="small"
                        onClick={handleSelectAllClick}
                        disabled={selectingAll || gettingData || totalRows === 0}
                    >
                        {selectingAll ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : (
                            t('ProductsTable.selectAll')
                        )}
                    </Button>
                </Box>
            )}
            {/* Action Bar */}
            <div
                className={classes.actionBarContainer}
                style={{ overflowX: 'auto', width: '100%' }}
            >
                <div className={classes.actionBar}>
                    <input
                        hidden
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".zip"
                    />
                    <Tooltip arrow title={t('ExportImageBatch.Tooltip')} placement="top">
                        <span>
                            <Button
                                size="small"
                                startIcon={<CloudUpload />}
                                variant="outlined"
                                endIcon={isFreemium && <span>👑</span>}
                                style={{ background: '#FFFFFF' }}
                                onClick={handleBulkImportProducts}
                            >
                                {t('ExportImageBatch.BatchImageExport')}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        size="small"
                        component={RouterLink}
                        startIcon={<CloudDownload />}
                        variant="outlined"
                        endIcon={isFreemium && <span>👑</span>}
                        style={{ background: '#FFFFFF' }}
                        to={routes.importProducts}
                    >
                        Importar CSV
                    </Button>
                    <Button
                        size="small"
                        startIcon={<CloudDownload />}
                        variant="outlined"
                        endIcon={isFreemium && <span>👑</span>}
                        style={{ background: '#FFFFFF' }}
                        onClick={handleExportProducts}
                    >
                        Exportar
                    </Button>
                </div>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    component="p"
                    className={classes.actionBarHint}
                >
                    {t('ExportImageBatch.AcceptedHint')}
                </Typography>
            </div>
            <Filters
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectedSubcategory={selectedSubcategory}
                setSelectedSubcategory={setSelectedSubcategory}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                selectedOrder={selectedOrder}
                setSelectedOrder={setSelectedOrder}
            />
            {/* Progress Banner */}
            {searchQuery === '' && (
                <>
                    <Box className={classes.progressBanner}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Box flex={1}>
                                <Box mb={1} fontSize="12px" color="#4A4A4A">
                                    Has subido {totalRows}/{maxProductLimit} productos
                                </Box>
                                <LinearProgress
                                    color="primary"
                                    variant="determinate"
                                    value={
                                        maxProductLimit > 0
                                            ? Math.min((totalRows / maxProductLimit) * 100, 100)
                                            : 0
                                    }
                                />
                            </Box>
                        </Box>
                        {isFreemium && 
                            <ButtonUpgradePlan size="small" fullWidth />
                        }
                    </Box>

                    <Grid container className={classes.statsGrid}>
                        <Grid item xs={4}>
                            <Card className={classes.statCard}>
                                <CardContent style={{ padding: 8 }}>
                                    <Box className={classes.statNumber} color="#173300">
                                        {totalRows}
                                    </Box>
                                    <Box className={classes.statLabel}>Total</Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={4}>
                            <Card className={classes.statCard}>
                                <CardContent style={{ padding: 8 }}>
                                    <Box className={classes.statNumber} color="#73EDB8">
                                        {activeProducts}
                                    </Box>
                                    <Box className={classes.statLabel}>Activos</Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={4}>
                            <Card className={classes.statCard}>
                                <CardContent style={{ padding: 8 }}>
                                    <Box className={classes.statNumber} color="#999">
                                        {hiddenProducts}
                                    </Box>
                                    <Box className={classes.statLabel}>Ocultos</Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            <Box className={classes.productList}>
                <Box mb={2}>
                    <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>Todos los productos</h3>
                </Box>

                {/* Select all when in bulk mode */}
                {bulkMode && (
                    <Box className={classes.bulkSelectRow}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAllSelected}
                                    indeterminate={isSomeSelected}
                                    onChange={handleSelectAllToggle}
                                    disabled={selectingAll || gettingData || totalRows === 0}
                                />
                            }
                            label={t('ProductsTable.selectAll')}
                        />
                        {selectedCount > 0 && (
                            <Typography variant="body2" color="textSecondary" className={classes.bulkSelectCount}>
                                {t(
                                    selectedCount === 1
                                        ? 'ProductsTable.bulkSelectionOfOne'
                                        : 'ProductsTable.bulkSelectionOfMany',
                                    {
                                        selected: selectedCount,
                                        total: totalRows,
                                    }
                                )}
                            </Typography>
                        )}
                    </Box>
                )}

                {products.length && products.map((product) => {
                    const isHiddenByLimit =
                        Boolean(product.is_hidden_by_limit) &&
                        storeData?.plan_features?.hide_excess_products;
                    return (
                        <Card
                            key={product.id}
                            className={classes.productCard}
                            style={isHiddenByLimit ? { opacity: 0.6 } : undefined}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                {bulkMode && (
                                    <Checkbox
                                        checked={selected.includes(product.id)}
                                        onChange={() => handleSelectProduct(product.id)}
                                    />
                                )}
                                <img
                                    src={
                                        product?.product_images?.length
                                            ? `${process.env.REACT_APP_IMAGES_API_URL}${Buffer.from(
                                                handleImageBase64(
                                                    getOrderedImages(product?.product_images)
                                                )
                                            ).toString('base64')}`
                                            : `https://ui-avatars.com/api/?name=${product?.name}&size=80&background=CAD7CA&color=fff`
                                    }
                                    alt={product.name}
                                    className={classes.productImage}
                                />

                                <Box className={classes.productInfo}>
                                    <Box className={classes.productTitle}>{product.name}</Box>
                                    <Box className={classes.productSku}>{product.sku}</Box>
                                    {isHiddenByLimit && (
                                        <Box
                                            style={{
                                                fontSize: 12,
                                                color: '#f44336',
                                                marginTop: 4,
                                            }}
                                        >
                                            Este producto está bloqueado por el límite de tu plan.
                                            Solo se podrán visualizar y eliminar.
                                        </Box>
                                    )}
                                </Box>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box className={classes.productPrice}>
                                    {formatNumber(product.price, storeData.currency)}
                                </Box>
                                <Box className={classes.productActions}>
                                    <div className={classes.productActionItem}>
                                        <Tooltip
                                            title={
                                                product.active === 1
                                                    ? t('ProductsTable.deactivateProduct')
                                                    : t('ProductsTable.activateProduct')
                                            }
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={product.active === 1}
                                                        onChange={() => toggleProduct(product)}
                                                        size="small"
                                                        color="primary"
                                                        disabled={isHiddenByLimit}
                                                        inputProps={{
                                                            'aria-label':
                                                                product.active === 1
                                                                    ? t('ProductsTable.deactivateProduct')
                                                                    : t('ProductsTable.activateProduct'),
                                                        }}
                                                    />
                                                }
                                                label=""
                                                style={{ margin: 0 }}
                                            />
                                        </Tooltip>
                                        <span className={classes.productActionLabel}>
                                            {product.active === 1
                                                ? t('ProductsTable.statusActive')
                                                : t('ProductsTable.statusInactive')}
                                        </span>
                                    </div>
                                    <div className={classes.productActionItem}>
                                        <Tooltip title={t('ProductsTable.headers.edit')}>
                                            <Button
                                                component={RouterLink}
                                                size="small"
                                                color="primary"
                                                to={routes.updateProduct + '/' + product.id}
                                                aria-label={t('ProductsTable.headers.edit')}
                                            >
                                                <Edit style={{ fontSize: 18 }} />
                                            </Button>
                                        </Tooltip>
                                        <span className={classes.productActionLabel}>
                                            {t('ProductsTable.editAction')}
                                        </span>
                                    </div>
                                </Box>
                            </div>
                        </Card>
                    );
                })}
                <Button
                    onClick={handleLoadMore}
                    disabled={!hasMore || loadingMore}
                    variant="outlined"
                    fullWidth
                >
                    {loadingMore ? 'Cargando...' : hasMore ? 'Cargar más' : 'No hay más productos'}
                </Button>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '16px',
                        gap: 8,
                    }}
                >
                    <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                        <Select
                            value={itemsPerPage}
                            onChange={(e) => setItemsPerPage(e.target.value)}
                            label="Items por página"
                        >
                            <MenuItem value={10}>10 por página</MenuItem>
                            <MenuItem value={20}>20 por página</MenuItem>
                            <MenuItem value={50}>50 por página</MenuItem>
                        </Select>
                    </FormControl>

                    <span style={{ fontSize: 13, color: '#555' }}>
                        Mostrando {itemsPerPage} de {totalRows} productos
                    </span>
                </div>
            </Box>

            {/* Bulk Actions Bar */}
            {bulkMode && selected.length > 0 && (
                <Paper
                    style={{
                        position: 'fixed',
                        bottom: 70,
                        left: 16,
                        right: 16,
                        padding: 12,
                        display: 'flex',
                        gap: 8,
                    }}
                >
                    <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={() => handleBulkAction('activate')}
                    >
                        Activar
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => handleBulkAction('deactivate')}
                    >
                        Desactivar
                    </Button>
                    <ConfirmableButton
                        variant="contained"
                        fullWidth
                        confirmMessage="¿Seguro que desea eliminar los productos seleccionados? Esta acción no se puede deshacer."
                        confirmButtonText="Eliminar"
                        cancelButtonText="Cancelar"
                        onClick={handleBulkDelete}
                        style={{
                            backgroundColor: colors.error.main,
                            color: colors.white,
                        }}
                    >
                        Eliminar
                    </ConfirmableButton>
                </Paper>
            )}
            {/* Upgrade Modal */}
            <ModalUpgradePlan open={openUpgradeModal} onClose={() => setOpenUpgradeModal(false)} />
        </Box>
    );
}

const mapStateToProps = ({ auth, common }) => ({
    storeId: auth.storeId,
    storeData: common.storeData,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ProductsTablePageMobile);
